// src/components/Notifications/NotificationDeepLinkHandler.tsx
// Au clic push / cold start : bascule le bon business puis ouvre la page cible.
// Évite le piège multi-business → /business-selection qui perd le deep-link.

import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  captureNotificationDeepLinkFromWindow,
  clearPendingNotifDeepLink,
  peekPendingNotifDeepLink,
  resolvePendingNavigationPath,
  type PendingNotifDeepLink,
} from '../../utils/notificationDeepLink';

const NotificationDeepLinkHandler: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const {
    user,
    userProfile,
    loading,
    currentBusiness,
    selectBusiness,
    switchToBusiness,
    isSuperAdmin,
    isCSM,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const handlingRef = useRef(false);

  // Avant paint / à chaque URL (ex. SW navigate) : sauver la cible avant * → /business-selection.
  useLayoutEffect(() => {
    captureNotificationDeepLinkFromWindow({
      pathname: location.pathname,
      search: location.search,
    });
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (loading || !user || !userProfile || handlingRef.current) return;

    const pending = peekPendingNotifDeepLink();
    if (!pending?.path) return;

    const run = async (target: PendingNotifDeepLink) => {
      handlingRef.current = true;
      try {
        const bizId = target.businessId;
        const allowed =
          !!bizId &&
          (isSuperAdmin ||
            isCSM ||
            (userProfile.businessIds || []).includes(bizId));

        if (bizId && allowed && currentBusiness?.id !== bizId) {
          if (isSuperAdmin || isCSM) {
            await switchToBusiness(bizId);
          } else {
            await selectBusiness(bizId);
          }
          // currentBusiness se met à jour → effet rejoué avec la même pending.
          handlingRef.current = false;
          return;
        }

        // Ancien lien sans businessId + multi-business sans sélection : garder la pending
        // pour après le choix manuel sur /business-selection.
        if (
          !bizId &&
          !currentBusiness &&
          !isSuperAdmin &&
          (userProfile.businessIds?.length ?? 0) > 1
        ) {
          handlingRef.current = false;
          return;
        }

        if (isCSM && !currentBusiness && !bizId) {
          handlingRef.current = false;
          return;
        }

        // Attendre d’avoir un business si un bizId était requis et qu’on vient de switcher
        if (bizId && currentBusiness?.id !== bizId && allowed) {
          handlingRef.current = false;
          return;
        }

        const dest = resolvePendingNavigationPath(target);
        clearPendingNotifDeepLink();
        if (`${location.pathname}${location.search}` !== dest) {
          navigate(dest, { replace: true });
        }
        handlingRef.current = false;
      } catch (error) {
        console.warn('[notif-deeplink] Impossible d’ouvrir la cible:', error);
        handlingRef.current = false;
      }
    };

    void run(pending);
  }, [
    loading,
    user,
    userProfile,
    currentBusiness,
    selectBusiness,
    switchToBusiness,
    isSuperAdmin,
    isCSM,
    navigate,
    location.pathname,
    location.search,
  ]);

  return <>{children}</>;
};

export default NotificationDeepLinkHandler;
